import * as $ from 'jquery';
import { AppModule } from '../../lib/app-module';
import { CustomRender, JekyllResource, getFilterCategories, getMiniExplorations, getOfferings, getPosts, renderJekyllResourcesAppend, renderJekyllResourcesReplace } from '../../lib/jekyll-resources';
import { shuffle } from '../../lib/shuffle';
import { startViewTransition } from '../../lib/view-transition-with-fallback';

export interface FilterConfig {
    introTemplateSelector: string;
    postTemplateSelector: string;
    offeringTemplateSelector: string;
    miniExplorationTemplateSelector: string;
    containerSelector: string;
    version: string;
}

interface FilterExport {
    renderTag: (tag: string) => Promise<void>;
    renderHash: () => Promise<void>;
}

const renderBadges: CustomRender['render'] =  (res: JekyllResource, el: Element): void => {
    const badges: Element[] = (res.tags ?? [])
        .filter((tag) => tag.startsWith('badge:'))
        .map((tag) => {
            const badge = tag.split(':')[1];
            const span = document.createElement('span');
            span.classList.add('__badge', '__bg_yellow');
            span.innerText = badge;
            return span;
        });
    el.append(...badges);
};

const createAppendClassRenderer = (selector: string, className: string): CustomRender => {
    const render: CustomRender['render'] = (_: JekyllResource, el: Element): void => {
        el.classList.add(className);
    };
    return {render, selector};
};

const badgeRenderer: CustomRender = {selector: '.dc-badges', render: renderBadges};

const getRandomItemsByTag = (resources: JekyllResource[], count: number, tag: string): JekyllResource[] => {
    const items = resources.filter((r) => r.tags?.includes(tag));
    const shuffled = shuffle(items);
    return shuffled.slice(0, count);
};

export class Filter extends AppModule<FilterConfig, FilterExport> {
    
    protected init(): void {
        this._export = {
            renderTag: (tag: string): Promise<void> => this.renderTag(tag),
            renderHash: (): Promise<void> => this.renderHash(),
        };
    }

    private async renderTag(tag: string): Promise<void> {
        const [
            filterCategories,
            allPosts,
            allOfferings,
            allExplorations
        ] = await Promise.all([
            getFilterCategories(this.config.version),
            getPosts(this.config.version),
            getOfferings(this.config.version),
            getMiniExplorations(this.config.version)]
        );
        const header = getRandomItemsByTag(filterCategories, 1, tag);       
        if (header.length === 0 ) {
            return;
        }
        const offerings = getRandomItemsByTag(allOfferings, 2, tag);
        const explorations = getRandomItemsByTag(allExplorations, 2, tag);
        const wantPostsCount = 6 - explorations.length;
        const posts = getRandomItemsByTag(allPosts, wantPostsCount, tag);
        const customRenders = this.buildCustomRenderers(header[0]);

        startViewTransition(() => {
            renderJekyllResourcesReplace(header, this.config.introTemplateSelector, this.config.containerSelector, customRenders);
            renderJekyllResourcesAppend(offerings, this.config.offeringTemplateSelector, this.config.containerSelector, customRenders);
            renderJekyllResourcesAppend(posts, this.config.postTemplateSelector, this.config.containerSelector, customRenders);
            renderJekyllResourcesAppend(explorations, this.config.miniExplorationTemplateSelector, this.config.containerSelector, customRenders);
        });

    }

    private buildCustomRenderers(headerResource: JekyllResource): CustomRender[] {
        const classTag = (headerResource.tags ?? []).find(t => t.startsWith('class:'));
        if (!classTag) {
            return [badgeRenderer];
        }
        const cssClass = classTag.split(':')[1];
        const appendClassRender = createAppendClassRenderer('.result', cssClass);
        return [badgeRenderer, appendClassRender];

    }

    private async renderHash(): Promise<void> {
        $(async () => {
            const tag = window.location.hash.startsWith('#') ? window.location.hash.substring(1) : undefined;
            if (tag) {
                await this.renderTag(tag);
            }    
        });
    }
}